import { isProductAvailable } from '@woolworthsnz/product';
import { IngredientProduct } from '@woolworthsnz/trader-api';

export interface GroupedIngredients {
	heading: string;
	ingredients: IngredientProduct[];
}

const mapIngredientsToRanks = (distinctRanks: number[], ingredients: IngredientProduct[]): IngredientProduct[] =>
	distinctRanks.map((rank) => {
		const itemsInRank = ingredients
			.filter((i) => Math.floor(i.rank || Infinity) === rank)
			.sort((a, b) => {
				// order by availability
				if (isProductAvailable(a.item) !== isProductAvailable(b.item)) {
					return isProductAvailable(a.item) ? -1 : 1;
				}

				// then by precise rank
				return (a.rank || Infinity) - (b.rank || Infinity);
			});

		return itemsInRank[0]; // return only 1 ingredient per rank
	});

const groupIngredientsWithHeadings = (ingredients: IngredientProduct[]): GroupedIngredients[] => {
	// create a general group for ingredients without a heading
	const grouped: GroupedIngredients[] = [
		{
			heading: 'Ingredients',
			ingredients: [],
		},
	];

	for (const i of ingredients) {
		const ingredient = { ...i }; // copy ingredient to not mutate it

		// update title if measurement values are specified
		if (ingredient.measurementType || ingredient.measurementValue) {
			ingredient.description = `${ingredient.description} - ${[
				ingredient.measurementValue,
				ingredient.measurementType,
			]
				.filter(Boolean)
				.join('&nbsp;')}`;
		}

		if (ingredient.ingredientType === 'Heading') {
			// for each heading we come across start a new group
			grouped.push({ heading: ingredient.description || '', ingredients: [] });
			continue;
		}

		// push the ingredient to the last group
		grouped[grouped.length - 1].ingredients.push(ingredient);
	}

	return grouped;
};

export const orderAndGroupIngredients = (ingredients: IngredientProduct[] | undefined): GroupedIngredients[] => {
	if (!ingredients?.length) {
		return [];
	}

	// get all distinct ranks rounded and sorted
	const distinctRanks = new Set(ingredients.map((i) => Math.floor(i.rank || Infinity)));
	const sortedRanks = Array.from(distinctRanks).sort((a, b) => a - b);

	// get items for each rank and order them on their precise rank and availability
	const ordered = mapIngredientsToRanks(sortedRanks, ingredients);

	// group the ingredients by headings - the first heading is just "Ingredients"
	return groupIngredientsWithHeadings(ordered);
};
